import { useMemo, useContext } from "react";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { useStyles } from "../StatusReportsTableDetails.styles";
import moment from "moment-timezone";
import { LocationsContext } from "contexts/LocationsContext/LocationsContext";
import { LocalserverStatusTableColumnsIdEnum } from "../StatusReportsTableDetailsInterfaces";
import { LocalserverStatusReportsResponse } from "api/queries/MillsAPI";
import { getDowntimeDurationInHoursMinutes } from "utilities/utilities";

const cellStyle = { padding: "16px 8px" };

interface IStatusReportsColumns extends Omit<IMainTableColumn, "render"> {
    render: (row: LocalserverStatusReportsResponse["local_server_status_reports"][0]) => string | number | JSX.Element;
}

export const useStatusReportsColumns = (): IStatusReportsColumns[] => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { millTimezone } = useContext(LocationsContext);

    const userTimezone = useMemo(() => moment.tz.guess(), []);

    const columns = useMemo(
        (): IStatusReportsColumns[] => [
            {
                id: LocalserverStatusTableColumnsIdEnum.TIME_STOP,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_network_details.table_headers.disconnection_time").toUpperCase()}
                    </Typography>
                ),
                customStyles: { ...cellStyle, width: "20%" },
                headerCustomStyles: cellStyle,
                hideSortIcon: false,
                render: (row) =>
                    moment
                        .utc(row.start_date)
                        .tz(millTimezone ?? userTimezone)
                        .format("MMM D, h:mm:ss A"),
            },
            {
                id: LocalserverStatusTableColumnsIdEnum.TIME_RESTART,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_network_details.table_headers.reconnection_time").toUpperCase()}
                    </Typography>
                ),
                customStyles: { ...cellStyle, width: "20%" },
                headerCustomStyles: cellStyle,
                hideSortIcon: true,
                render: (row) =>
                    row.end_date ? (
                        moment
                            .utc(row.end_date)
                            .tz(millTimezone ?? userTimezone)
                            .format("MMM D, h:mm:ss A")
                    ) : (
                        <Typography variant="subtitle1" className={classes.waiting}>
                            {t("locations.secondary_code_requirements_table.waiting")}
                        </Typography>
                    ),
            },
            {
                id: "",
                label: "",
                customStyles: { ...cellStyle, width: "45%" },
                headerCustomStyles: cellStyle,
                hideSortIcon: true,
                render: (row) => "",
            },
            {
                id: LocalserverStatusTableColumnsIdEnum.DURATION,
                label: (
                    <Typography variant="subtitle2" className={classes.columnHeader}>
                        {t("locations.mill_network_details.table_headers.disconnection_duration").toUpperCase()}
                    </Typography>
                ),
                customStyles: { ...cellStyle, width: "15%" },
                headerCustomStyles: cellStyle,
                hideSortIcon: false,
                render: (row) =>
                    row.duration_in_seconds ? (
                        getDowntimeDurationInHoursMinutes(row.duration_in_seconds, true)
                    ) : (
                        <Typography variant="subtitle1" className={classes.toBeDefined}>
                            {t("locations.secondary_code_requirements_table.to_be_defined")}
                        </Typography>
                    ),
            },
        ],
        [classes.columnHeader, t, millTimezone, userTimezone, classes.toBeDefined, classes.waiting],
    );

    return columns;
};
